import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSession } from "next-auth/react";
import { getCookie, setCookie } from "cookies-next";
import { listenToFirestoreMeetings } from "@/store/apps/meetings/MeetingThunks";
import {
  completeEnrichment,
  fetchUserData
} from "@/store/apps/userData/UserDataSlice";
import { store } from "@/store/store";
import { getProfilePicture } from "@/lib/utils/profilePicture";
import { cleanName } from "@/lib/utils/nameUtils";
import EnrichmentLoadingScreen from "./EnrichmentLoadingScreen";

// RC Live enrichment (fresh LinkedIn scrape) can take up to 60s. The notifier
// hides immediately when the server signals completion via Pusher or when
// requiresManualInput flips true (handing off to VanityModal). These timers
// are only safety nets so a stuck request doesn't trap the user forever.
const MAX_SHOW_TIME = 70000; // 70s — must exceed RC Live's 60s timeout so Pusher arrives first
const FALLBACK_TIMEOUT = 80000; // absolute safety net
const EMERGENCY_EXIT_DELAY = 15000; // user can dismiss after 15s if stuck

export default function EnrichmentNotifier({
  onActiveChange = () => {},
  initialShowEnrichmentLoading = false,
  initialBookingHandoff = false,
  verificationUiActive = false,
  profileSetupMode = false,
  hasMeetingRequest = false,
}) {
  const dispatch = useDispatch();

  const { data: session } = useSession();
  const userSub = session?.user?.sub;
  const messageFirstSignup = ["google", "email"].includes(
    session?.user?.signupProvider
  );
  const profile = useSelector(state => state.user.profile);
  const profileImage = getProfilePicture(profile) || session?.user?.image || null;
  const profileName = cleanName(
    [profile?.given_name, profile?.family_name]
      .filter(Boolean)
      .join(" ") ||
      profile?.name ||
      [session?.user?.given_name, session?.user?.family_name]
        .filter(Boolean)
        .join(" ") ||
      session?.user?.name ||
      ""
  );

  const [showNotification, setShowNotification] = useState(false);
  const [introStarted, setIntroStarted] = useState(false);
  const [introTextComplete, setIntroTextComplete] = useState(false);
  const [devTestActive, setDevTestActive] = useState(() => {
    if (typeof window === "undefined") return false;
    if (process.env.NODE_ENV !== "development") return false;
    return new URLSearchParams(window.location.search).has("testEnrichment");
  });
  const [devTestCompleting, setDevTestCompleting] = useState(false);
  const [forceHide, setForceHide] = useState(false);
  const [initialDelayComplete, setInitialDelayComplete] = useState(false);
  const [allowEmergencyExit, setAllowEmergencyExit] = useState(false);
  const [setupCompletionFinished, setSetupCompletionFinished] = useState(false);
  const [bookingHandoffActive, setBookingHandoffActive] = useState(
    initialBookingHandoff,
  );

  const showTimeRef = useRef(null);
  const fallbackTimeoutRef = useRef(null);
  const emergencyTimerRef = useRef(null);
  const hasShownRef = useRef(false);
  const wasInRealSetupRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const enrichmentStartTimeRef = useRef(null);
  const handoffTimerRef = useRef(null);
  const profileSetupModeRef = useRef(false);
  const meetingRequestRef = useRef(false);
  if (profileSetupMode || bookingHandoffActive) {
    profileSetupModeRef.current = true;
  }
  if (hasMeetingRequest || initialBookingHandoff) {
    meetingRequestRef.current = true;
  }

  const [handingOff, setHandingOff] = useState(false);

  const [showEarlyLoading, setShowEarlyLoading] = useState(() => {
    if (initialShowEnrichmentLoading) return true;
    if (typeof window === "undefined") return false;
    return Boolean(userSub) && getCookie("meex_enrichment_complete") !== userSub;
  });

  // Completion is stored per account so switching users in one browser cannot
  // suppress the new account's initial setup screen.
  useLayoutEffect(() => {
    if (!userSub) return;
    const setupWasCompleted = getCookie("meex_enrichment_complete") === userSub;
    setShowEarlyLoading(
      setupWasCompleted ? false : initialShowEnrichmentLoading
    );
  }, [userSub, initialShowEnrichmentLoading]);

  const [hasReceivedInitialData, setHasReceivedInitialData] = useState(false);

  const enrichmentStatus = useSelector(state => state.user.enrichmentStatus);
  const needsEnrichment = useSelector(state => state.user.needsEnrichment);
  const isInitializationComplete = useSelector(
    state => state.user.isInitializationComplete
  );
  const portfolioMigrated = useSelector(state => state.user.portfolioMigrated);
  const requiresManualInput = useSelector(
    state => state.user.requiresManualInput
  );
  const isUnderReview = useSelector(state => state.user.isUnderReview);
  const verificationHandoffActive = useSelector(
    state => state.user.verificationHandoffActive
  );
  const contactEmailConfirmationRequested = useSelector(
    state => state.user.contactEmailConfirmationRequested
  );
  const contactEmailConfirmationCompleted = useSelector(
    state => state.user.contactEmailConfirmationCompleted
  );
  const showSentModal = useSelector(state => state.user.showSentModal);
  const isProcessingBooking = useSelector(
    state => state.user.isProcessingBooking
  );
  const bookingEmailHandoffCompleted = useSelector(
    state => state.user.bookingEmailHandoffCompleted
  );
  // Once booking processing finishes, SentModal owns the meeting/email
  // confirmation handoff. Do not bring the profile setup overlay back over it
  // if profile state refreshes while the seeker confirms their email. The
  // completion flag keeps it suppressed after the modal is dismissed.
  const bookingConfirmationActive =
    bookingEmailHandoffCompleted ||
    (showSentModal && !isProcessingBooking);
  const emailConfirmationPending =
    contactEmailConfirmationRequested ||
    (isInitializationComplete &&
      profile?.email_acknowledged === false &&
      Boolean(profile?.email) &&
      !isUnderReview);
  const emailAcknowledgedHandoffComplete =
    !bookingHandoffActive &&
    (contactEmailConfirmationCompleted ||
      (messageFirstSignup && profile?.email_acknowledged === true));

  const hasProfileEnrichmentStep =
    (profileSetupMode || profileSetupModeRef.current || bookingHandoffActive) &&
    !(hasMeetingRequest || meetingRequestRef.current) &&
    !isUnderReview;
  // Email/Google users must see the code modal first. The enrichment loader
  // starts only after LinkedIn message verification dispatches startEnrichment.
  const showMessageSignupLoader =
    messageFirstSignup &&
    (enrichmentStatus === "processing" || verificationHandoffActive) &&
    !requiresManualInput;

  // Calculate derived values first
  const isEnrichmentComplete =
    !verificationHandoffActive &&
    (hasProfileEnrichmentStep
      ? enrichmentStatus === "completed" || portfolioMigrated === true
      : (enrichmentStatus === "completed" ||
        isInitializationComplete ||
        portfolioMigrated === true));

  useEffect(() => {
    if (!isEnrichmentComplete || devTestActive || !wasInRealSetupRef.current) {
      setSetupCompletionFinished(false);
      return undefined;
    }
  }, [isEnrichmentComplete, devTestActive]);

  const handleSetupCompletionAnimationComplete = useCallback(() => {
    if (devTestActive) {
      setDevTestCompleting(false);
      setDevTestActive(false);
      return;
    }
    wasInRealSetupRef.current = false;
    profileSetupModeRef.current = false;
    setSetupCompletionFinished(true);
  }, [devTestActive]);

  const hasUserData =
    isEnrichmentComplete ||
    portfolioMigrated === true ||
    needsEnrichment === false ||
    profile?.linkedin?.id ||
    profile?.id;

  const shouldShow =
    !forceHide &&
    !hasCompletedRef.current &&
    !bookingConfirmationActive &&
    !emailConfirmationPending &&
    !emailAcknowledgedHandoffComplete &&
    (!messageFirstSignup ||
      showMessageSignupLoader ||
      verificationHandoffActive ||
      bookingHandoffActive) &&
    // Once we know the next step is the manual-URL modal, hand off immediately
    // so VanityModal opens in the same render frame the loader unmounts —
    // otherwise the user sees the dashboard for a few seconds in between.
    (!requiresManualInput || bookingHandoffActive) &&
    // If the user is already under admin review, skip the loader entirely —
    // the review modal opens directly without any loading screen.
    !isUnderReview &&
    enrichmentStatus !== "failed" &&
    (verificationHandoffActive ||
      bookingHandoffActive ||
      (hasReceivedInitialData &&
        needsEnrichment === true &&
        !isEnrichmentComplete &&
        initialDelayComplete));
  const showSetupCompletion =
    isEnrichmentComplete &&
    !isProcessingBooking &&
    !devTestActive &&
    wasInRealSetupRef.current &&
    !setupCompletionFinished;
  const showMeetingSetupHandoff =
    meetingRequestRef.current &&
    profileSetupModeRef.current &&
    isProcessingBooking &&
    !requiresManualInput &&
    !verificationHandoffActive;
  const isBookingVerificationStep =
    bookingHandoffActive && requiresManualInput && !verificationUiActive;

  useEffect(() => {
    if (!bookingConfirmationActive) return;
    setBookingHandoffActive(false);
    try {
      window.sessionStorage.removeItem("meex:booking-setup-network-seed");
    } catch {
      // Storage is optional; the booking confirmation can continue normally.
    }
  }, [bookingConfirmationActive]);

  useEffect(() => {
    if (
      !isProcessingBooking &&
      !profileSetupMode &&
      !hasProfileEnrichmentStep &&
      !showSetupCompletion
    ) {
      profileSetupModeRef.current = false;
      meetingRequestRef.current = false;
    }
  }, [
    hasProfileEnrichmentStep,
    isProcessingBooking,
    profileSetupMode,
    showSetupCompletion
  ]);

  useEffect(() => {
    if (shouldShow && !devTestActive && !isEnrichmentComplete) {
      wasInRealSetupRef.current = true;
    }
  }, [shouldShow, devTestActive, isEnrichmentComplete]);

  // True only when Redux has confirmed this user actually needs enrichment —
  // distinguishes a quick returning-user flash (Case 1) from a real new-user wait (Case 2).
  const isConfirmedNewUser = hasReceivedInitialData && needsEnrichment === true;

  // Keep the first-time loader mounted while persisted Redux state is being
  // restored and the notifier's effects catch up. `needsEnrichment` can already
  // be true on the first render, before `hasReceivedInitialData` is updated.
  const isLoadingUserState =
    showEarlyLoading &&
    !isEnrichmentComplete &&
    !bookingConfirmationActive &&
    !forceHide &&
    (!requiresManualInput || bookingHandoffActive) &&
    !isUnderReview &&
    !emailConfirmationPending &&
    !emailAcknowledgedHandoffComplete &&
    (needsEnrichment === null || (needsEnrichment === true && !shouldShow));

  useEffect(() => {
    if (shouldShow || devTestActive) {
      setIntroStarted(true);
      setIntroTextComplete(false);
    }
  }, [shouldShow, devTestActive]);

  const keepIntroVisible =
    !emailConfirmationPending &&
    !emailAcknowledgedHandoffComplete &&
    !isEnrichmentComplete &&
    !hasCompletedRef.current &&
    introStarted &&
    !introTextComplete;

  const handleIntroTextComplete = useCallback(() => {
    setIntroTextComplete(true);
  }, []);

  const isLoaderVisible =
    !bookingConfirmationActive &&
    !emailAcknowledgedHandoffComplete &&
    !hasCompletedRef.current &&
    (!messageFirstSignup ||
      showMessageSignupLoader ||
      verificationHandoffActive) &&
    (shouldShow ||
      devTestActive ||
      (isLoadingUserState && !messageFirstSignup));

  // Force hide function - wrapped in useCallback to prevent ESLint warnings
  const forceHideNotification = useCallback(() => {
    setShowNotification(false);
    setShowEarlyLoading(false);
    setForceHide(true);
    setAllowEmergencyExit(false);
    setIntroStarted(false);
    setIntroTextComplete(true);

    // Clear all timeouts
    if (showTimeRef.current) {
      clearTimeout(showTimeRef.current);
      showTimeRef.current = null;
    }
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    if (emergencyTimerRef.current) {
      clearTimeout(emergencyTimerRef.current);
      emergencyTimerRef.current = null;
    }

    // Reset enrichment tracking
    enrichmentStartTimeRef.current = null;

    // Start meetings if we have a session
    if (userSub) {
      dispatch(listenToFirestoreMeetings(userSub));
    }
  }, [userSub, dispatch]);

  // Handle backdrop click — always blocked. Previously we had an emergency
  // exit after 15s, but users would accidentally click through and land on
  // a half-loaded dashboard. The fallback timeout still hides the loader if
  // it ever genuinely gets stuck.
  const handleBackdropClick = () => {
    return;
  };

  // Track when enrichment starts and set emergency timer
  useEffect(() => {
    if (shouldShow && !enrichmentStartTimeRef.current) {
      enrichmentStartTimeRef.current = Date.now();
      setAllowEmergencyExit(false);

      // Set emergency exit timer
      emergencyTimerRef.current = setTimeout(() => {
        setAllowEmergencyExit(true);
      }, EMERGENCY_EXIT_DELAY);

      return () => {
        if (emergencyTimerRef.current) {
          clearTimeout(emergencyTimerRef.current);
          emergencyTimerRef.current = null;
        }
      };
    }
  }, [shouldShow]);

  // Reset emergency exit when enrichment completes normally
  useEffect(() => {
    if (isEnrichmentComplete || !shouldShow) {
      enrichmentStartTimeRef.current = null;
      setAllowEmergencyExit(false);
      if (emergencyTimerRef.current) {
        clearTimeout(emergencyTimerRef.current);
        emergencyTimerRef.current = null;
      }
    }
  }, [isEnrichmentComplete, shouldShow]);

  // Absolute fallback timeout - always hide after 10 seconds
  useEffect(() => {
    if (showNotification && !fallbackTimeoutRef.current) {
      fallbackTimeoutRef.current = setTimeout(() => {
        forceHideNotification();
      }, FALLBACK_TIMEOUT);
    }

    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    };
  }, [showNotification, forceHideNotification]);

  useEffect(() => {
    if (
      (enrichmentStatus && enrichmentStatus !== "idle") ||
      profile?.sub ||
      isInitializationComplete === true ||
      portfolioMigrated === true ||
      needsEnrichment === true ||
      (needsEnrichment === false && profile?.sub)
    ) {
      setHasReceivedInitialData(true);
    }
  }, [
    enrichmentStatus,
    needsEnrichment,
    profile?.sub,
    isInitializationComplete,
    portfolioMigrated
  ]);

  useEffect(() => {
    setTimeout(() => setInitialDelayComplete(true), 100);
  }, []);

  useEffect(() => {
    if (isEnrichmentComplete) {
      hasCompletedRef.current = true;
      setShowEarlyLoading(false);
    }
  }, [isEnrichmentComplete]);

  useEffect(() => {
    if (hasReceivedInitialData && !initialDelayComplete) {
      setInitialDelayComplete(true);
    }
  }, [hasReceivedInitialData, initialDelayComplete]);

  useEffect(() => {
    if ((isEnrichmentComplete || portfolioMigrated) && userSub) {
      setCookie("meex_enrichment_complete", userSub, {
        maxAge: 60 * 60 * 24 * 365
      });

      forceHideNotification();
    }
  }, [isEnrichmentComplete, portfolioMigrated, userSub, forceHideNotification]);

  useEffect(() => {
    if (shouldShow && !isEnrichmentComplete) {
      setShowNotification(true);
      hasShownRef.current = true;

      // Record when we started showing
      showTimeRef.current = setTimeout(() => {
        forceHideNotification();
      }, MAX_SHOW_TIME);

      return () => {
        if (showTimeRef.current) {
          clearTimeout(showTimeRef.current);
          showTimeRef.current = null;
        }
      };
    } else {
      setShowNotification(false);
      if (userSub && hasUserData && hasReceivedInitialData) {
        dispatch(listenToFirestoreMeetings(userSub));
      }
    }
  }, [
    shouldShow,
    isEnrichmentComplete,
    userSub,
    dispatch,
    hasUserData,
    hasReceivedInitialData,
    forceHideNotification
  ]);

  // Additional safeguard: if we've shown for too long, force hide.
  // BUT only when we have a definitive next-step signal (enrichment
  // completed OR manual input required). Otherwise we'd hide the loader
  // mid-enrichment and leak the dashboard before the next modal opens.
  useEffect(() => {
    if (
      hasShownRef.current &&
      hasUserData &&
      (isEnrichmentComplete || requiresManualInput)
    ) {
      const safeguardTimeout = setTimeout(() => {
        forceHideNotification();
      }, 0);

      return () => clearTimeout(safeguardTimeout);
    }
  }, [
    hasUserData,
    isEnrichmentComplete,
    requiresManualInput,
    forceHideNotification
  ]);

  useEffect(() => {
    if (hasUserData && !isEnrichmentComplete && !hasCompletedRef.current) {
      setForceHide(false);
    }
  }, [hasUserData, isEnrichmentComplete]);

  useEffect(() => {
    if (verificationHandoffActive) {
      hasCompletedRef.current = false;
      setForceHide(false);
      setShowEarlyLoading(false);
      setShowNotification(true);
      hasShownRef.current = true;
    }
  }, [verificationHandoffActive]);

  // Pusher is the fast path, but an event can be missed while a tab is
  // connecting or resuming from the background. Re-read the persisted user
  // state during the handoff so refresh is never required to close the loader.
  useEffect(() => {
    if (!verificationHandoffActive || !userSub) return undefined;

    let cancelled = false;
    const recoverCompletion = async () => {
      await dispatch(fetchUserData(true, { silent: true }));
      if (cancelled) return;

      const state = store.getState().user;
      if (
        state.isInitializationComplete === true ||
        state.portfolioMigrated === true ||
        state.needsEnrichment === false
      ) {
        dispatch(completeEnrichment({ profile: state.profile }));
      }
    };

    recoverCompletion();
    const timer = setInterval(recoverCompletion, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [dispatch, userSub, verificationHandoffActive]);

  // Dev-only test mode: visit any page with ?testEnrichment to preview the loader
  // and its full six-step completion animation.
  useEffect(() => {
    if (!devTestActive) return;
    hasShownRef.current = true;
    const completion = setTimeout(() => {
      setDevTestCompleting(true);
    }, 8000);
    return () => clearTimeout(completion);
  }, [devTestActive]);

  useEffect(() => {
    onActiveChange(isLoaderVisible);
  }, [isLoaderVisible, onActiveChange]);

  // Case 3: when requiresManualInput flips true after we've been visible, keep the
  // backdrop alive for 400ms so the user doesn't see a dashboard flash before
  // VanityModal opens (DashboardLayout delays it by 350ms).
  useEffect(() => {
    if (requiresManualInput && hasShownRef.current && !handingOff) {
      setHandingOff(true);
      handoffTimerRef.current = setTimeout(() => {
        setHandingOff(false);
      }, 400);
    }
    return () => {
      if (handoffTimerRef.current) {
        clearTimeout(handoffTimerRef.current);
        handoffTimerRef.current = null;
      }
    };
  }, [requiresManualInput, handingOff]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeRef.current) clearTimeout(showTimeRef.current);
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
      if (emergencyTimerRef.current) clearTimeout(emergencyTimerRef.current);
      if (handoffTimerRef.current) clearTimeout(handoffTimerRef.current);
    };
  }, []);

  if (
    (emailConfirmationPending || emailAcknowledgedHandoffComplete) &&
    !devTestActive
  )
    return null;

  if (bookingConfirmationActive && !devTestActive) return null;

  if (verificationUiActive && !devTestActive) return null;

  if (
    (!shouldShow &&
      !handingOff &&
      !devTestActive &&
      !verificationHandoffActive &&
      !keepIntroVisible &&
      !(isLoadingUserState && !(messageFirstSignup || bookingHandoffActive)) &&
      !showSetupCompletion &&
      !showMeetingSetupHandoff) ||
    (isEnrichmentComplete &&
      !devTestActive &&
      !keepIntroVisible &&
      !(isLoadingUserState && !(messageFirstSignup || bookingHandoffActive)) &&
      !showSetupCompletion &&
      !showMeetingSetupHandoff &&
      !bookingHandoffActive)
  )
    return null;

  return (
    <EnrichmentLoadingScreen
      isComplete={showSetupCompletion || devTestCompleting}
      isFinalSetupReady={portfolioMigrated === true}
      profileSetupMode={
        profileSetupMode || profileSetupModeRef.current || bookingHandoffActive
      }
      hasMeetingRequest={hasMeetingRequest || meetingRequestRef.current}
      hasProfileEnrichmentStep={hasProfileEnrichmentStep}
      bookingHandoffIntro={bookingHandoffActive && !profileSetupMode}
      verificationMode={isBookingVerificationStep}
      isSchedulingMeeting={showMeetingSetupHandoff}
      preserveNetworkSession={bookingHandoffActive || meetingRequestRef.current}
      disablePortal={bookingHandoffActive}
      userImage={profileImage}
      userName={profileName}
      onBackdropClick={handleBackdropClick}
      onIntroTextComplete={handleIntroTextComplete}
      onCompletionAnimationComplete={handleSetupCompletionAnimationComplete}
    />
  );
}
